import { ErrorOutline } from '@mui/icons-material';
import { Box, Button, Checkbox, Grid, Typography } from '@mui/material';
import { first } from 'lodash';
import React, { startTransition, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomButton } from 'src/components/button/Button';
import { FilterList } from 'src/components/filterList';
import { Input } from 'src/components/input';
import { SimpleDialog } from 'src/components/modal';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Progress } from 'src/components/progress/Progress';
import { Snackbar } from 'src/components/snackbar';
import { StyledTypography } from 'src/components/subHeader/SubHeaderWithSorting.styled';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import { PATHS } from 'src/constants/paths';
import {
  useCreateProductCategory,
  useDeleteProductCategory,
  useFilterAndSorting,
  useGetCCAssets,
  useModal,
  useUpdateProductsCategory,
} from 'src/hooks';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { useNavigateBlocker } from 'src/hooks/navigation';
import { IFilter, IProduct } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';
import { getProductNameFromAssetId } from 'src/utils/gcf-helper';

import {
  ButtonContainer,
  CheckboxLabel,
  InputContainer,
  ManageCategoryLink,
  ProductContainer,
  ProductGridContainer,
  ProductSectionHeader,
  StyleDivider,
} from './AddProductCategoryPage.styled';
import { useAddProductCategoryPage } from './hooks/AddProductCategoryPage.hook';

export const AddProductCategoryPage = () => {
  const { data: assets = [] } = useGetCCAssets();
  const { isViewOnly } = useUserRole();
  const createProductCategory = useCreateProductCategory();
  const updateProductCategory = useUpdateProductsCategory();
  const deleteProductCategory = useDeleteProductCategory();
  const [inputBoxStatus, setInputBoxStatus] = React.useState('');
  const [productCategoryName, setProductCategoryName] = React.useState('');
  const [selectedProducts, setSelectedProducts] = React.useState<IProduct[]>(
    [],
  );
  const [selectAll, setSelectAll] = React.useState(false);
  const [errorData, setErrorData] = React.useState<any>(null);
  const {
    productCardData,
    productCardLoading,
    onChangeParams,
    productCategories,
    productVariationTypes,
  } = useAddProductCategoryPage();
  const navigate = useNavigate();
  const CloseModal = useModal();
  const skipBlockRef = useRef(false);

  const skipNextBlock = useCallback(() => {
    skipBlockRef.current = true;
  }, []);

  const filters: IFilter[] = React.useMemo(
    () => [
      {
        label: 'Product category',
        options: productCategories.map((item) => ({
          label: item.name,
          value: item.id,
        })),
        isLoading: false,
        customCta: (
          <ManageCategoryLink
            component="button"
            variant="body2"
            onClick={() => navigate(PATHS.PRODUCT_CATEGORIES_MANAGE)}>
            {lang.manageCategory}
          </ManageCategoryLink>
        ),
      },
      {
        label: 'Status',
        options: [
          { label: 'Active', value: 1 },
          { label: 'Inactive', value: 0 },
        ],
        isLoading: false,
      },
      {
        label: 'Variation types',
        options: productVariationTypes.map((item) => ({
          label: item.name || 'None',
          value: item.id,
        })),
        isLoading: false,
      },
      {
        label: 'Components',
        options: [
          { label: ' Assigned to component', value: 'true' },
          { label: ' Unassigned to component', value: 'false' },
        ],
        isLoading: false,
      },
    ],
    [productCategories, productVariationTypes, navigate],
  );

  const selectedCount = selectedProducts.length;

  const { onChangeKeyword, onRemoveKeyword, onReset } =
    useFilterAndSorting(filters);

  const handleSelectAllClick = () => {
    setSelectAll(!selectAll);
    if (!selectAll) {
      setSelectedProducts([...productCardData]);
    } else {
      setSelectedProducts([]);
    }
  };

  const handleCategoryNameChnage = (value: string) => {
    startTransition(() => {
      setProductCategoryName(value.trim());
    });
  };

  const handleProductCheckboxChange = (product: IProduct) => {
    const isSelected = selectedProducts.some(
      (selectedProduct) =>
        selectedProduct.product_code === product.product_code,
    );

    if (isSelected) {
      setSelectedProducts(
        selectedProducts.filter(
          (selectedProduct) =>
            selectedProduct.product_code !== product.product_code,
        ),
      );
    } else {
      setSelectedProducts([...selectedProducts, product]);
    }
  };

  const hasUnsavedChanges =
    productCategoryName.trim() !== '' || selectedProducts.length > 0;

  useNavigateBlocker(
    () =>
      !skipBlockRef.current &&
      hasUnsavedChanges &&
      !createProductCategory.isLoading &&
      !updateProductCategory.isLoading,
  );

  const handleModalConfirm = () => {
    skipNextBlock();
    navigate(PATHS.PRODUCTS);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      CloseModal.onOpenModal();
    } else {
      handleModalConfirm();
    }
  };

  const handleDone = async () => {
    try {
      if (!productCategoryName) return;
      const request = { product_name: productCategoryName };
      const result = await createProductCategory.mutateAsync(request);

      try {
        if (result && selectedProducts) {
          const payload = {
            data: selectedProducts.map((product) => ({
              resource: COLLECTIONS.PRODUCTS,
              method: 'PATCH',
              id: product.product_code,
              data: {
                product_category_id: [
                  ...product.product_category_id,
                  result.id,
                ],
                asset_id: product.asset_id,
                narrative: (
                  narrativeGenerators[COLLECTIONS.PRODUCT_CATEGORIES][
                    USER_ACTIONS.CREATE
                  ] as (productCategoryName: string) => string
                )(productCategoryName || ''),
              },
            })),
          };
          await updateProductCategory.mutateAsync(payload.data);
        }
        skipNextBlock();
        navigate(PATHS.PRODUCTS);
        Snackbar.success(`${productCategoryName} ${lang.successfullyCreated}`);
      } catch (transactionError: any) {
        await deleteProductCategory.mutateAsync(Number(result.id));
        Snackbar.error(
          `${productCategoryName} ${lang.failedToCreateProductCategory}`,
        );
        const errorObject =
          selectedProducts[transactionError.response.data.details?.index || 0];

        setErrorData(errorObject);

        if (transactionError.response.data.error.includes('Duplicate entry')) {
          setInputBoxStatus('error');
        }

        if (
          transactionError.response.data.error.includes('One of the asset_ids')
        ) {
          Snackbar.warning(lang.failedToCreateProductCategory);
        }
      }
    } catch (error: any) {
      const errorObject =
        selectedProducts[error.response.data.details?.index || 0];

      setErrorData(errorObject);

      if (error.response.data.error.includes('Duplicate entry')) {
        setInputBoxStatus('error');
      }

      if (error.response.data.error.includes('One of the asset_ids')) {
        Snackbar.warning(lang.failedToCreateProductCategory);
      }
    }
  };

  return (
    <Grid>
      <Box sx={{ position: 'absolute', top: 0, right: 0, margin: 3 }}>
        {isViewOnly && <ViewOnlyAlert />}
      </Box>
      <Typography variant="h1" gutterBottom>
        Create new product category
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={12} md={3}>
          <FilterList
            filters={filters}
            onChangeKeyword={onChangeKeyword}
            onRemoveKeyword={onRemoveKeyword}
            onChangeFilter={onChangeParams}
            showReset
            onReset={onReset}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <Typography variant="h4" component="h2" gutterBottom>
            Create category
          </Typography>
          <StyleDivider />
          <InputContainer>
            <Input
              label="Product category name"
              onChange={handleCategoryNameChnage}
              value={productCategoryName}
              status={inputBoxStatus}
              setInputBoxStatus={setInputBoxStatus}
            />
          </InputContainer>
          {!productCardLoading && productCardData.length >= 0 && (
            <StyledTypography paddingTop={4}>
              {`${lang.Showing} ${productCardData.length}/${productCardData.length}`}
            </StyledTypography>
          )}
          <ProductContainer>
            {productCardData.length > 0 && (
              <ProductSectionHeader>
                <Typography
                  variant="h4"
                  component="h2"
                  gutterBottom
                  flexGrow={1}>
                  Select products
                </Typography>
                <Typography
                  variant="body1"
                  color="grey"
                  marginRight={1}
                  marginBottom={0}
                  paddingTop={'6px'}>
                  {`${selectedCount} Selected`}
                </Typography>
                <Button variant="text" onClick={handleSelectAllClick}>
                  {selectAll ? 'Deselect all' : 'Select all'}
                </Button>
              </ProductSectionHeader>
            )}
            <StyleDivider />
            {productCardLoading ? (
              <Progress />
            ) : productCardData.length === 0 ? (
              <NoResultAlert />
            ) : (
              <ProductGridContainer container spacing={1}>
                {productCardData.map((product: IProduct) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    key={product.product_code}>
                    <CheckboxLabel
                      label={
                        <span
                          style={{
                            color:
                              errorData?.product_code === product.product_code
                                ? 'red'
                                : 'inherit',
                            display: 'flex',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                          }}>
                          {product.name_override ||
                            product?.productVariations.find(
                              (variation) =>
                                variation.product_code ===
                                product?.product_code,
                            )?.name ||
                            getProductNameFromAssetId(
                              assets,
                              first(product.asset_id),
                            ) ||
                            product.product_name}
                          {errorData?.product_code === product.product_code && (
                            <ErrorOutline />
                          )}
                        </span>
                      }
                      control={
                        <Checkbox
                          checked={selectedProducts.some(
                            (selectedProduct) =>
                              selectedProduct.product_code ===
                              product.product_code,
                          )}
                          onChange={() => handleProductCheckboxChange(product)}
                        />
                      }
                    />
                  </Grid>
                ))}
              </ProductGridContainer>
            )}

            {!isViewOnly && (
              <ButtonContainer>
                <CustomButton variant="text" onClick={handleCancel}>
                  {lang.cancel}
                </CustomButton>
                <Button
                  variant="contained"
                  onClick={handleDone}
                  disabled={productCategoryName === '' || isViewOnly}>
                  {lang.create}
                </Button>
              </ButtonContainer>
            )}
          </ProductContainer>
        </Grid>
      </Grid>

      <SimpleDialog
        open={CloseModal.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.discardModal.title}
        onClose={CloseModal.onCloseModal}
        onClickCancel={CloseModal.onCloseModal}
        modalSize="sm"
        onClickConfirm={handleModalConfirm}
      />
    </Grid>
  );
};
